import { useState, useRef, useEffect } from 'react'
import { ExternalLink, ArrowUpDown, ArrowUp, ArrowDown, Activity, Info, Bot } from 'lucide-react'
import { clsx } from 'clsx'
import type { TargetAccount, Owner, SortField, SortOrder, ColumnConfig, ScoreExplanation } from '../types'
import { AVAILABLE_COLUMNS } from '../types'
import { CompanyLogo } from './CompanyLogo'
import { ScoreBadge } from './ScoreBadge'
import { StatusBadge } from './BulkActionBar'

interface AccountsTableProps {
  accounts: TargetAccount[]
  owners: Owner[]
  sortField: SortField
  sortOrder: SortOrder
  visibleColumns: string[]
  selectedAccountIds: string[]
  onSort: (field: SortField) => void
  onSelectAccount: (id: string) => void
  onSelectAllAccounts: (ids: string[]) => void
  onShowSignals?: (account: TargetAccount) => void
  onShowAIAssistant?: (account: TargetAccount) => void
}

// HubSpot portal ID - FrankieOne
const HUBSPOT_PORTAL_ID = '8836420'

// HubSpot object type IDs
// 0-1 = contacts, 0-2 = companies, 0-3 = deals, 0-5 = tickets
const HUBSPOT_COMPANY_OBJECT_TYPE = '0-2'

function getHubSpotUrl(companyId: string) {
  return `https://app.hubspot.com/contacts/${HUBSPOT_PORTAL_ID}/record/${HUBSPOT_COMPANY_OBJECT_TYPE}/${companyId}`
}

function getOwnerName(ownerId: string | null, owners: Owner[]): string {
  if (!ownerId) return '-'
  const owner = owners.find((o) => o.id === ownerId)
  if (owner) {
    return owner.first_name && owner.last_name
      ? `${owner.first_name} ${owner.last_name}`
      : owner.email?.split('@')[0] || 'Unknown'
  }
  return '-'
}

function formatDate(dateStr: string | null): string {
  if (!dateStr) return '-'
  try {
    const date = new Date(dateStr)
    return date.toLocaleDateString('en-AU', { day: '2-digit', month: 'short', year: 'numeric' })
  } catch {
    return '-'
  }
}

function formatNumber(num: number | null): string {
  if (num === null || num === undefined) return '-'
  return num.toLocaleString()
}

function formatCurrency(num: number | null): string {
  if (num === null || num === undefined) return '-'
  if (num >= 1000000000) {
    return `$${(num / 1000000000).toFixed(1)}B`
  }
  if (num >= 1000000) {
    return `$${(num / 1000000).toFixed(1)}M`
  }
  if (num >= 1000) {
    return `$${(num / 1000).toFixed(0)}K`
  }
  return `$${num.toLocaleString()}`
}

interface SortHeaderProps {
  label: string
  field: SortField
  currentField: SortField
  sortOrder: SortOrder
  onSort: (field: SortField) => void
  title?: string
  className?: string
  scoreExplanation?: ScoreExplanation
}

// Score Explanation Tooltip Component
function ScoreTooltip({ explanation, onClose }: { explanation: ScoreExplanation; onClose: () => void }) {
  const tooltipRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (tooltipRef.current && !tooltipRef.current.contains(event.target as Node)) {
        onClose()
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [onClose])

  return (
    <div
      ref={tooltipRef}
      className="absolute z-50 top-full left-0 mt-2 w-72 bg-[var(--bg-secondary)] border border-[var(--border-color)] rounded-lg shadow-xl p-4"
      onClick={(e) => e.stopPropagation()}
    >
      <div className="space-y-3">
        <div>
          <h4 className="font-semibold text-sm text-[var(--text-primary)]">{explanation.title}</h4>
          <p className="text-xs text-[var(--text-muted)] mt-1">{explanation.description}</p>
        </div>

        <div>
          <h5 className="text-xs font-medium text-[var(--text-secondary)] mb-1">Calculation</h5>
          <p className="text-xs text-[var(--text-muted)]">{explanation.calculation}</p>
        </div>

        {explanation.thresholds && (
          <div>
            <h5 className="text-xs font-medium text-[var(--text-secondary)] mb-2">Thresholds</h5>
            <div className="space-y-1.5">
              {explanation.thresholds.map((t) => (
                <div key={t.label} className="flex items-start gap-2 text-xs">
                  <ScoreBadge
                    score={t.label.toLowerCase()}
                    type={t.label === 'Excellent' || t.label === 'Good' || t.label === 'Medium' || t.label === 'Low' ? 'overall' : 'factor'}
                  />
                  <span className="text-[var(--text-muted)] flex-1">{t.condition}</span>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

function SortHeader({ label, field, currentField, sortOrder, onSort, title, className, scoreExplanation }: SortHeaderProps) {
  const isActive = currentField === field
  const [showTooltip, setShowTooltip] = useState(false)

  return (
    <th
      className={clsx(
        'px-4 py-3 text-left text-xs font-semibold text-[var(--text-muted)] uppercase tracking-wide cursor-pointer select-none hover:bg-[var(--border-color)] transition-colors relative',
        className
      )}
      onClick={() => onSort(field)}
      title={!scoreExplanation ? title : undefined}
    >
      <div className="flex items-center gap-1 justify-center">
        {label}
        {scoreExplanation && (
          <button
            onClick={(e) => {
              e.stopPropagation()
              setShowTooltip(!showTooltip)
            }}
            className="p-0.5 rounded hover:bg-[var(--bg-secondary)] text-[var(--text-muted)] hover:text-primary transition-colors"
            title="Click for details"
          >
            <Info size={12} />
          </button>
        )}
        {isActive ? (
          sortOrder === 'asc' ? (
            <ArrowUp size={12} className="text-primary" />
          ) : (
            <ArrowDown size={12} className="text-primary" />
          )
        ) : (
          <ArrowUpDown size={12} className="opacity-40" />
        )}
      </div>
      {showTooltip && scoreExplanation && (
        <ScoreTooltip explanation={scoreExplanation} onClose={() => setShowTooltip(false)} />
      )}
    </th>
  )
}

// Render cell content based on column type
function renderCell(
  account: TargetAccount,
  column: ColumnConfig,
  owners: Owner[],
  onShowAIAssistant?: (account: TargetAccount) => void
): React.ReactNode {
  // Special handling for company name (first column with logo + AI button)
  if (column.id === 'name') {
    return (
      <div className="flex items-center gap-2">
        <a
          href={getHubSpotUrl(account.id)}
          target="_blank"
          rel="noopener noreferrer"
          className="flex items-center gap-3 group min-w-0"
        >
          <CompanyLogo domain={account.domain} name={account.name} />
          <div className="min-w-0">
            <div className="font-medium text-[var(--text-primary)] group-hover:text-primary truncate flex items-center gap-1">
              {account.name}
              <ExternalLink size={12} className="opacity-0 group-hover:opacity-100 transition-opacity" />
            </div>
            {account.domain && (
              <div className="text-xs text-[var(--text-muted)] truncate">{account.domain}</div>
            )}
          </div>
        </a>
        {onShowAIAssistant && (
          <button
            onClick={() => onShowAIAssistant(account)}
            className="flex-shrink-0 inline-flex items-center justify-center w-7 h-7 rounded-md text-[var(--text-muted)] hover:text-purple-500 hover:bg-purple-500/10 transition-colors"
            title="AI Assistant"
          >
            <Bot size={14} />
          </button>
        )}
      </div>
    )
  }

  // Special handling for account_status
  if (column.id === 'account_status') {
    return <StatusBadge status={account.account_status} />
  }

  // Special handling for owner
  if (column.id === 'owner') {
    return (
      <span className="text-sm text-[var(--text-secondary)]">
        {getOwnerName(account.hubspot_owner_id, owners)}
      </span>
    )
  }

  // Get the value from the account
  const field = column.field as keyof TargetAccount
  const value = account[field]

  // Handle based on column type
  switch (column.type) {
    case 'score':
      // Determine score type based on column id
      let scoreType: 'factor' | 'conversion' | 'overall' = 'factor'
      if (column.id === 'icp_fit_score') scoreType = 'conversion'
      if (column.id === 'overall_icp_score') scoreType = 'overall'
      return <ScoreBadge score={value as string | null} type={scoreType} />

    case 'badge':
      if (!value) return <span className="text-[var(--text-muted)]">-</span>
      return (
        <span className="inline-block px-2 py-1 bg-[var(--bg-tertiary)] rounded text-xs text-[var(--text-secondary)]">
          {String(value)}
        </span>
      )

    case 'date':
      return (
        <span className="text-sm text-[var(--text-secondary)]">
          {formatDate(value as string | null)}
        </span>
      )

    case 'number':
      // Special handling for revenue
      if (column.id === 'revenue') {
        return (
          <span className="text-sm text-[var(--text-secondary)]">
            {formatCurrency(value as number | null)}
          </span>
        )
      }
      return (
        <span className="text-sm text-[var(--text-secondary)]">
          {formatNumber(value as number | null)}
        </span>
      )

    case 'text':
    default:
      if (!value) return <span className="text-[var(--text-muted)]">-</span>
      return (
        <span className="text-sm text-[var(--text-secondary)] truncate block">
          {String(value)}
        </span>
      )
  }
}

export function AccountsTable({ accounts, owners, sortField, sortOrder, visibleColumns, selectedAccountIds, onSort, onSelectAccount, onSelectAllAccounts, onShowSignals, onShowAIAssistant }: AccountsTableProps) {
  if (accounts.length === 0) {
    return (
      <div className="bg-[var(--bg-secondary)] border border-[var(--border-color)] rounded-lg p-8 text-center">
        <p className="text-[var(--text-muted)]">No accounts found matching your criteria.</p>
      </div>
    )
  }

  // Get the columns to display based on visibleColumns, maintaining order from AVAILABLE_COLUMNS
  const columnsToShow = AVAILABLE_COLUMNS.filter((col) => visibleColumns.includes(col.id))

  const visibleIds = accounts.map((a) => a.id)
  const allVisibleSelected = visibleIds.length > 0 && visibleIds.every((id) => selectedAccountIds.includes(id))
  const someVisibleSelected = visibleIds.some((id) => selectedAccountIds.includes(id)) && !allVisibleSelected

  return (
    <div className="bg-[var(--bg-secondary)] border border-[var(--border-color)] rounded-lg overflow-hidden">
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead className="bg-[var(--bg-tertiary)] border-b border-[var(--border-color)]">
            <tr>
              {/* Checkbox header */}
              <th className="px-3 py-3 w-[44px]">
                <input
                  type="checkbox"
                  checked={allVisibleSelected}
                  ref={(el) => {
                    if (el) el.indeterminate = someVisibleSelected
                  }}
                  onChange={() => onSelectAllAccounts(visibleIds)}
                  className="w-4 h-4 rounded border-[var(--border-color)] text-primary focus:ring-primary/20 cursor-pointer"
                  title={allVisibleSelected ? 'Deselect all' : 'Select all'}
                />
              </th>
              {columnsToShow.map((column) => (
                <SortHeader
                  key={column.id}
                  label={column.label}
                  field={column.field as SortField}
                  currentField={sortField}
                  sortOrder={sortOrder}
                  onSort={onSort}
                  title={column.description}
                  scoreExplanation={column.scoreExplanation}
                  className={clsx(
                    column.width,
                    column.type === 'score' || column.type === 'number' ? 'text-center' : ''
                  )}
                />
              ))}
              <th className="px-4 py-3 text-center text-xs font-semibold text-[var(--text-muted)] uppercase tracking-wide w-[60px]">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-[var(--border-color)]">
            {accounts.map((account) => {
              const isSelected = selectedAccountIds.includes(account.id)
              return (
                <tr
                  key={account.id}
                  className={clsx(
                    'transition-colors',
                    isSelected
                      ? 'bg-primary/5 hover:bg-primary/10'
                      : 'hover:bg-[var(--bg-tertiary)]'
                  )}
                >
                  {/* Checkbox cell */}
                  <td className="px-3 py-3">
                    <input
                      type="checkbox"
                      checked={isSelected}
                      onChange={() => onSelectAccount(account.id)}
                      className="w-4 h-4 rounded border-[var(--border-color)] text-primary focus:ring-primary/20 cursor-pointer"
                    />
                  </td>
                  {columnsToShow.map((column) => (
                    <td
                      key={column.id}
                      className={clsx(
                        'px-4 py-3',
                        column.type === 'score' || column.type === 'number' ? 'text-center' : ''
                      )}
                    >
                      {renderCell(account, column, owners, onShowAIAssistant)}
                    </td>
                  ))}
                  {/* Actions */}
                  <td className="px-4 py-3 text-center">
                    <div className="flex items-center justify-center gap-1">
                      {onShowSignals && (
                        <button
                          onClick={() => onShowSignals(account)}
                          className="inline-flex items-center justify-center w-8 h-8 rounded-md text-[var(--text-muted)] hover:text-primary hover:bg-primary/10 transition-colors"
                          title="View Intent Signals"
                        >
                          <Activity size={16} />
                        </button>
                      )}
                      <a
                        href={getHubSpotUrl(account.id)}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center justify-center w-8 h-8 rounded-md text-[var(--text-muted)] hover:text-[var(--text-primary)] hover:bg-[var(--bg-tertiary)] transition-colors"
                        title="Open in HubSpot"
                      >
                        <ExternalLink size={16} />
                      </a>
                    </div>
                  </td>
                </tr>
              )
            })}
          </tbody>
        </table>
      </div>
    </div>
  )
}
